import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export interface RoleProtectedRouteProps {
  children: React.ReactElement;
  allowedRoles: string[];
  fallbackPath?: string;
}

/**
 * RoleProtectedRoute
 * Guards routes against unauthorized role access.
 * Non-super-admins or roles not listed in allowedRoles are intercepted and redirected
 * to their designated operational workstation or fallback destination.
 */
export const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  allowedRoles,
  fallbackPath
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // 1. While loading session / auth state, display centered spinner
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  // 2. Unauthenticated requests redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3. Role Assertion
  if (!allowedRoles.includes(user.role)) {
    // Role-specific operational home redirection
    if (user.role === 'kitchen') {
      return <Navigate to="/kitchen" replace />;
    }
    if (user.role === 'rider') {
      return <Navigate to="/driver/dashboard" replace />;
    }
    // Default fallback for staff, store managers, and general roles
    return <Navigate to={fallbackPath || '/orders'} replace />;
  }

  // 4. Authorized
  return children;
};

export default RoleProtectedRoute;
